// Progress: for a student, their own, batch by batch; for a batch's teachers and admins, every
// student at a glance and each one in full. Attendance, recordings opened, practice tests, mock exams
// and the topics where objective answers go wrong most.
import { Link } from '../router.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Chip, Empty, ErrorBox, Loading, Progress, Section, useRpc } from '../ui/components.jsx';
import { percent, scoreText } from '../ui/question.jsx';
import { dateShort, dateTime, istDate, plural } from '../format.js';
import { STATUS_LABEL } from './attendance.jsx';

const pctText = (v) => (v === null || v === undefined ? '–' : `${Number(v)}%`);
const STATUS_TONE = { absent: 'bad', online: 'ok', offline: 'ok' };

function Stat({ title, big, children, value, max, label }) {
  return (
    <div class="pcard">
      <h3>{title}</h3>
      <p class="pcard-big">{big}</p>
      {max ? <Progress value={value} max={max} label={label || title} /> : null}
      <p class="pcard-sub">{children}</p>
    </div>
  );
}

// The cards for one student in one batch (the same for the student and for their teachers).
export function ProgressCards({ p, staff, idBase }) {
  const at = p.attendance;
  const rec = p.recordings;
  const pr = p.practice;
  const weak = (p.topics || []).filter((t) => Number(t.rate) < 100).slice(0, 5);
  const strong = (p.topics || []).filter((t) => Number(t.rate) >= 75 && !weak.includes(t)).slice(-3).reverse();
  const mockAvg = p.mocks.length ? Math.round(p.mocks.reduce((t, m) => t + percent(m.score, m.max_score), 0) / p.mocks.length) : null;
  return (
    <>
      <div class="progress-grid">
        <Stat title="Attendance" big={pctText(at.rate)} value={Number(at.online) + Number(at.offline)} max={Number(at.marked)} label="Classes attended">
          {Number(at.marked)
            ? `Present at ${Number(at.online) + Number(at.offline)} of ${plural(Number(at.marked), 'class', 'classes')} (${at.online} online, ${at.offline} offline)`
            : 'No attendance taken yet'}
        </Stat>
        <Stat title="Recordings" big={`${rec.opened} / ${rec.total}`} value={Number(rec.opened)} max={Number(rec.total)} label="Recordings opened">
          {Number(rec.total) ? `${plural(Number(rec.opened), 'class', 'classes')} opened` : 'No classes yet'}
        </Stat>
        <Stat title="Practice tests" big={pctText(pr.average)} value={Number(pr.done)} max={Number(pr.set)} label="Practice tests done">
          {Number(pr.set) ? `${pr.done} of ${plural(Number(pr.set), 'test')} done${pr.average !== null && pr.average !== undefined ? ' · average best score' : ''}` : 'No practice tests yet'}
        </Stat>
        <Stat title="Mock exams" big={mockAvg === null ? '–' : `${mockAvg}%`}>
          {p.mocks.length ? `Average of ${plural(p.mocks.length, 'mock')}` : 'No marked mocks yet'}
          {p.mocks_waiting ? ` · ${p.mocks_waiting} being marked` : ''}
        </Stat>
      </div>
      {p.mocks.length ? (
        <Section title="Mock exams" id={`${idBase}-mocks`}>
          <ul class="list">
            {p.mocks.map((m) => (
              <li key={m.quiz_id} class="list-row mock-line">
                <span class="list-text"><strong>{m.title}</strong><small>Submitted {dateShort(istDate(m.submitted_at))}</small></span>
                <span class="mock-score">{scoreText(m.score, m.max_score)} <strong>{percent(m.score, m.max_score)}%</strong></span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {weak.length ? (
        <Section title="Topics to work on" id={`${idBase}-topics`}>
          <p class="muted">From {staff ? 'their' : 'your'} objective answers in tests whose answers are out. Lowest first.</p>
          <ol class="topic-list">
            {weak.map((t) => (
              <li key={t.topic} class={Number(t.rate) < 50 ? 'is-low' : ''}>
                <span class="topic-name">{t.topic}</span>
                <span class="topic-detail">{t.right} of {plural(Number(t.answered), 'question')} right</span>
                <span class="item-pct"><span class="bar" role="img" aria-label={`${t.rate}% right`}><span style={{ width: `${t.rate}%` }} /></span><span>{t.rate}%</span></span>
              </li>
            ))}
          </ol>
          {strong.length ? <p class="field-hint">Strongest: {strong.map((t) => `${t.topic} (${t.rate}%)`).join(', ')}.</p> : null}
        </Section>
      ) : null}
      {p.recent_attendance.length ? (
        <Section title="Recent classes" id={`${idBase}-att`}>
          <ul class="list attendance-list">
            {p.recent_attendance.map((r) => (
              <li key={r.session_id} class="list-row">
                <span class="list-text"><strong>{r.title || 'Class'}</strong><small>{dateTime(r.starts_at)}</small></span>
                <Chip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Chip>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- a student's own */
export function MyProgress() {
  const { data, error, loading, reload } = useRpc('my_progress', {});
  return (
    <Shell title="My progress" backTo="/profile" backLabel="Profile">
      <p class="muted">How you’re doing in each batch: classes attended, recordings opened, tests and mock exams.</p>
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.batches.length ? <Empty icon="chart" title="Nothing yet">Once you’re in a batch, your progress shows here.</Empty> : null}
      {(data ? data.batches : []).map((b) => (
        <section class="progress-batch" key={b.batch.id} aria-labelledby={`pb-${b.batch.id}`}>
          <h2 id={`pb-${b.batch.id}`}><span class="subject subject-xs">{b.batch.subject || '•'}</span>{b.batch.name}</h2>
          <ProgressCards p={b} idBase={`pb-${b.batch.id}`} />
        </section>
      ))}
    </Shell>
  );
}

/* ---------------------------------------------------------------- a batch, for its staff */
export function BatchProgress({ id }) {
  const { data, error, loading, reload } = useRpc('staff_batch_progress', { p_batch_id: id }, [id]);
  return (
    <Shell backTo={`/batches/${id}`} backLabel="Batch">
      {loading && !data ? <Loading rows={5} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <p class="eyebrow">{data.batch.name}</p>
          <h1 class="page-title">Progress</h1>
          <p class="muted">{plural(data.students.length, 'student')} · attendance taken for {plural(Number(data.sessions_marked), 'class', 'classes')}. Open a student for their tests, mocks and topics.</p>
          {!data.students.length ? <Empty icon="user" title="No students yet">Students enrolled in the batch show here.</Empty> : (
            <div class="table-wrap" tabIndex={0} role="region" aria-label="Students’ progress">
              <table class="data-table progress-table">
                <thead><tr>
                  <th scope="col">Student</th><th scope="col" class="num">Attendance</th><th scope="col" class="num">Recordings</th>
                  <th scope="col" class="num">Practice</th><th scope="col" class="num">Mocks</th><th scope="col">Last active</th>
                </tr></thead>
                <tbody>
                  {data.students.map((s) => {
                    const mockAvg = s.mocks.length ? Math.round(s.mocks.reduce((t, m) => t + percent(m.score, m.max_score), 0) / s.mocks.length) : null;
                    const lowAtt = s.attendance.rate !== null && s.attendance.rate !== undefined && Number(s.attendance.rate) < 75;
                    const lowAvg = s.practice.average !== null && s.practice.average !== undefined && Number(s.practice.average) < 50;
                    return (
                      <tr key={s.id}>
                        <th scope="row">
                          <Link href={`/batches/${id}/progress/${s.id}`}>{s.name}</Link>
                          {lowAtt || lowAvg ? <span class="flags">{lowAtt ? <Chip tone="bad">Attendance under 75%</Chip> : null}{lowAvg ? <Chip tone="bad">Tests under 50%</Chip> : null}</span> : null}
                        </th>
                        <td class="num">{pctText(s.attendance.rate)}<small>{Number(s.attendance.marked) ? ` of ${s.attendance.marked}` : ''}</small></td>
                        <td class="num">{s.recordings.opened} / {s.recordings.total}</td>
                        <td class="num">{pctText(s.practice.average)}<small> · {s.practice.done}/{s.practice.set}</small></td>
                        <td class="num">{mockAvg === null ? '–' : `${mockAvg}%`}</td>
                        <td>{s.last_active ? dateShort(istDate(s.last_active)) : 'Not yet'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </Shell>
  );
}

export function StudentProgress({ batchId, personId }) {
  const { data, error, loading, reload } = useRpc('staff_student_progress', { p_batch_id: batchId, p_person_id: personId }, [batchId, personId]);
  return (
    <Shell backTo={`/batches/${batchId}/progress`} backLabel="Progress">
      {loading && !data ? <Loading rows={5} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <p class="eyebrow">{data.batch.name}</p>
          <h1 class="page-title">{data.student.name}</h1>
          <p class="muted">{data.student.email}{data.last_active ? ` · last active ${dateTime(data.last_active)}` : ''}</p>
          <ProgressCards p={data} staff idBase="sp" />
          <p class="row"><Link class="btn btn-line btn-sm" href={`/batches/${batchId}/progress`}><Icon name="prev" /><span>All students</span></Link></p>
        </>
      ) : null}
    </Shell>
  );
}
